import React, { useEffect, useState } from 'react'
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, MarkdownEditor } from '@duck/ui'
import { useTar } from '@/modules/i18n'

interface DialogContentEditorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  initialValue: string
  onSave: (value: string) => Promise<void>
}

/** Shared markdown editor for cohort and assignment messages. */
export const DialogContentEditorDialog: React.FC<DialogContentEditorDialogProps> = ({ open, onOpenChange, title, description, initialValue, onSave }) => {
  const { t } = useTar()
  const [value, setValue] = useState(initialValue)
  const [saving, setSaving] = useState(false)
  // Snapshot initialValue only when the dialog opens: the editor is uncontrolled and keyed on `open`,
  // so a later refetch must not reset `value`/`dirty` out from under text already typed.
  useEffect(() => { if (open) setValue(initialValue) }, [open])

  const trimmed = value.trim()
  const dirty = trimmed !== initialValue.trim()

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(trimmed)
      onOpenChange(false)
    } catch {
      // Caller toasts; keep the dialog open so edits aren't lost.
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!saving) onOpenChange(next) }}>
      <DialogContent width="lg" className="flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex h-[300px] shrink-0">
          {/* MarkdownEditor is uncontrolled; key on open so reopening resets to initialValue. */}
          <MarkdownEditor
            key={open ? 'open' : 'closed'}
            defaultValue={initialValue}
            onChange={setValue}
            mode={saving ? 'view' : 'edit'}
            mdClassName="bg-background"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} disabled={!dirty || saving}>{saving ? t('partner.messageEditorSaving') : t('common.save')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
